import React, { useState, useMemo } from 'react';
import { Col, Card, Table, Form, Button, ButtonGroup, Badge } from 'react-bootstrap';
import UserTypeBadge from '../../utils/UserTypeBadge';

const UserTable = ({ users, userTypes, onEdit, onChangePassword, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');

  const filteredUsers = useMemo(() => {
    let result = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          user.username?.toLowerCase().includes(term) ||
          user.displayName?.toLowerCase().includes(term) ||
          user.emailAddress?.toLowerCase().includes(term) ||
          user.agencyName?.toLowerCase().includes(term)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return result;
  }, [users, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <Card className="shadow">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Col md={8}><h5 className="mb-0">Users ({filteredUsers.length})</h5></Col>
        <Col md={4}>
          <Form.FloatingLabel label="Search Users" controlId="search-users">
            <Form.Control
              type="text"
              size="sm"
              className="search-box"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.FloatingLabel>
        </Col>
      </Card.Header>
      <Card.Body className="p-0">
        <Table striped hover responsive className="mb-0">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>
                Username<SortIndicator field="username" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('displayName')}>
                Display Name<SortIndicator field="displayName" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('emailAddress')}>
                Email<SortIndicator field="emailAddress" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('agencyName')}>
                Agency<SortIndicator field="agencyName" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('userType')}>
                Type<SortIndicator field="userType" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('enrollmentDate')}>
                Enrolled<SortIndicator field="enrollmentDate" />
              </th>
              <th className="text-center" style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  {searchTerm ? 'No users match your search.' : 'No users found.'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.displayName}</td>
                  <td>{user.emailAddress}</td>
                  <td>{user.agencyName}</td>
                  <td>
                    <UserTypeBadge userType={user.userType}
                      userAgency={user.agencyName}
                    />
                  </td>
                  <td>{user.enrollmentDate}</td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button variant="outline-primary" onClick={() => onEdit(user)} title="Edit user">
                        Edit
                      </Button>
                      <Button variant="outline-primary" onClick={() => onChangePassword(user)} title="Change password">
                        Password
                      </Button>
                      <Button variant="outline-danger" onClick={() => onDelete(user)} title="Delete user">
                        Delete
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default UserTable;
